/**
 * PlaceCore public mappings — labels, icons, and formatters.
 * Single source of truth for all public display of PlaceCore data.
 *
 * v2: Canonical intents now come from public.intents table.
 * This file provides fallback config for public rendering when
 * the DB isn't queried (e.g. static display).
 */

/* ─── Intent config (key → label + icon) ─── */
export const INTENT_CONFIG: Record<string, { label: string; iconName: string }> = {
  // Canonical v1 intents
  "vandring":        { label: "Vandring",           iconName: "Mountain" },
  "cykling":         { label: "Cykling",            iconName: "Compass" },
  "skidor":          { label: "Skidor",             iconName: "Mountain" },
  "paddling":        { label: "Paddling",           iconName: "Compass" },
  "fiske":           { label: "Fiske",              iconName: "TreePine" },
  "utsiktsplats":    { label: "Utsikt",             iconName: "Mountain" },
  "stillhet-ro":     { label: "Stillhet & ro",      iconName: "Heart" },
  "naturupplevelse": { label: "Naturupplevelse",    iconName: "TreePine" },
  "cafe":            { label: "Café",               iconName: "Compass" },
  "gardsbutik":      { label: "Gårdsbutik",         iconName: "Compass" },
  "smaka-lokalt":    { label: "Smaka lokalt",       iconName: "Compass" },
  "marknad":         { label: "Marknad",            iconName: "Compass" },
  "guidning":        { label: "Guidning",           iconName: "Palette" },
  "hantverk":        { label: "Hantverk",           iconName: "Palette" },
  "lara":            { label: "Lära",               iconName: "Palette" },
  "familj":          { label: "Familj",             iconName: "Users" },
  "lek":             { label: "Lek",                iconName: "Users" },
  "umgas":           { label: "Umgås",              iconName: "Users" },
  // Legacy keys (backward compat)
  "natur-friluftsliv": { label: "Natur & friluftsliv", iconName: "TreePine" },
  "kultur-historia":   { label: "Kultur & historia",   iconName: "Palette" },
  "lugn-helg":         { label: "Lugn helg",           iconName: "Moon" },
  stillness: { label: "Stillhet & ro", iconName: "Heart" },
  excursion: { label: "Utflykt", iconName: "Compass" },
  family: { label: "Familj", iconName: "Users" },
  nature: { label: "Natur & friluftsliv", iconName: "TreePine" },
  hiking: { label: "Vandring", iconName: "Mountain" },
  culture: { label: "Kultur & historia", iconName: "Palette" },
  calm_weekend: { label: "Lugn helg", iconName: "Moon" },
};

/* ─── Season labels ─── */
export const SEASON_LABELS: Record<string, string> = {
  spring: "vår",
  summer: "sommar",
  autumn: "höst",
  winter: "vinter",
};

export const SEASON_ORDER = ["spring", "summer", "autumn", "winter"] as const;

/* ─── Duration labels (lt_1h treated same as short) ─── */
export const DURATION_LABELS: Record<string, string> = {
  short: "Kort stopp",
  lt_1h: "Kort stopp",
  "1_3h": "1–3 timmar",
  half_day: "Halvdag",
  full_day: "Heldag",
};

/* ─── Tag-to-intent fallback (conservative) ─── */
const TAG_TO_INTENT: Record<string, string> = {
  natur: "naturupplevelse",
  friluftsliv: "naturupplevelse",
  vandring: "vandring",
  barnvänligt: "familj",
  lugn: "stillhet-ro",
  kultur: "guidning",
  historia: "guidning",
  utflykt: "vandring",
  fäbod: "stillhet-ro",
  landsbygd: "stillhet-ro",
};

/** Normalize seasons: handle legacy `season` (string) → `seasons` (array) */
export function normalizeSeasons(pc: Record<string, any>): string[] {
  if (Array.isArray(pc.seasons) && pc.seasons.length > 0) {
    return pc.seasons.filter((s: string) => SEASON_LABELS[s]);
  }
  if (typeof pc.season === "string" && pc.season) {
    if (pc.season === "multi") return ["spring", "summer", "autumn", "winter"];
    if (SEASON_LABELS[pc.season]) return [pc.season];
  }
  return [];
}

/** Format seasons array → "vår, höst" (sorted) */
export function formatSeasons(seasons: string[]): string {
  return seasons
    .filter((s) => SEASON_LABELS[s])
    .sort((a, b) => SEASON_ORDER.indexOf(a as any) - SEASON_ORDER.indexOf(b as any))
    .map((s) => SEASON_LABELS[s])
    .join(", ");
}

/** Format seasons with dash for compact display → "vår–höst" (first–last) */
export function formatSeasonsDash(seasons: string[]): string {
  const sorted = seasons
    .filter((s) => SEASON_LABELS[s])
    .sort((a, b) => SEASON_ORDER.indexOf(a as any) - SEASON_ORDER.indexOf(b as any));
  if (sorted.length === 0) return "";
  if (sorted.length === 1) return SEASON_LABELS[sorted[0]];
  return `${SEASON_LABELS[sorted[0]]}–${SEASON_LABELS[sorted[sorted.length - 1]]}`;
}

/** Format duration key → label (null if missing) */
export function formatDuration(duration: string | null | undefined): string | null {
  if (!duration) return null;
  return DURATION_LABELS[duration] ?? null;
}

/** Get label for a single intent */
export function formatIntent(intent: string): string {
  return INTENT_CONFIG[intent]?.label ?? intent;
}

/** Map tags → intent keys (deduped, max 5) */
export function tagsToIntents(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const tag of tags) {
    const intent = TAG_TO_INTENT[tag.toLowerCase()];
    if (intent && !seen.has(intent)) {
      seen.add(intent);
      result.push(intent);
      if (result.length >= 5) break;
    }
  }
  return result;
}
